export type StyleCategory = 'internal-ops' | 'field-operations';
export type DensityLevel = 'low' | 'medium' | 'high';
export type ProductKey = 'projectManagerApp' | 'checkInPlatform';

export interface StyleDefinition {
  id: string;
  name: string;
  category: StyleCategory;
  description: string;
  extractionNotes: {
    basedOnConcepts: string[];
    adaptedFor: string;
  };
  moodBoard: {
    keywords: string[];
    imageHints: string[];
  };
  tokens: {
    colors: {
      primary: string;
      background: string;
      surface: string;
      text: string;
      muted: string;
      success: string;
      warning: string;
      danger: string;
    };
    radius: number;
    spacing: number;
    density: DensityLevel;
  };
}

export const PRODUCT_CATEGORY_MAP: Record<ProductKey, StyleCategory> = {
  projectManagerApp: 'internal-ops',
  checkInPlatform: 'field-operations',
};

export const PRODUCT_LABELS: Record<ProductKey, string> = {
  projectManagerApp: 'Project Manager App',
  checkInPlatform: 'Check-In Platform',
};

export const styleDefinitions: StyleDefinition[] = [
  {
    id: 'soft-neutral-pro',
    name: 'Soft Neutral Pro',
    category: 'internal-ops',
    description: 'Calm, professional, low-noise UI for tables, forms, dashboards, and daily information work.',
    extractionNotes: {
      basedOnConcepts: ['style taxonomy', 'semantic color grouping', 'UX context-fit', 'anti-pattern avoidance'],
      adaptedFor: 'Keeps the main internal workspace readable over long office sessions while preserving a soft blue emphasis for actions and states.',
    },
    moodBoard: {
      keywords: ['calm dashboards', 'soft blue accents', 'airy panels', 'quiet data workspace'],
      imageHints: ['modern office dashboard neutral blue', 'soft daylight workspace interface', 'clean admin panel white blue', 'professional data desk calm'],
    },
    tokens: {
      colors: {
        primary: '#3C67D8',
        background: '#F4F6FB',
        surface: '#FFFFFF',
        text: '#192132',
        muted: '#667085',
        success: '#1F8F5F',
        warning: '#C97C18',
        danger: '#D14343',
      },
      radius: 16,
      spacing: 8,
      density: 'low',
    },
  },
  {
    id: 'data-grid-focus',
    name: 'Data Grid Focus',
    category: 'internal-ops',
    description: 'Sharper, structure-first direction built for quotation, invoice, project, and task data density.',
    extractionNotes: {
      basedOnConcepts: ['design-system generation workflow', 'product-type matching', 'semantic layout patterns', 'density tuning'],
      adaptedFor: 'Pushes clarity in filter bars and tables so operational data can be scanned quickly without losing production polish.',
    },
    moodBoard: {
      keywords: ['structured rows', 'clear dividers', 'steel blue utility', 'dense operational panels'],
      imageHints: ['spreadsheet dashboard enterprise grid', 'structured office records interface', 'financial operations workstation blue gray', 'admin panel rows crisp contrast'],
    },
    tokens: {
      colors: {
        primary: '#2251B4',
        background: '#EEF2F7',
        surface: '#FFFFFF',
        text: '#111827',
        muted: '#5B6472',
        success: '#0F8A63',
        warning: '#C06C16',
        danger: '#C93535',
      },
      radius: 12,
      spacing: 8,
      density: 'high',
    },
  },
  {
    id: 'executive-dashboard',
    name: 'Executive Dashboard',
    category: 'internal-ops',
    description: 'KPI-oriented, lightly elevated, management-friendly styling for overview and decision screens.',
    extractionNotes: {
      basedOnConcepts: ['dashboard pattern recommendation', 'hierarchy-first typography', 'status-led palette design', 'pre-delivery review mindset'],
      adaptedFor: 'Balances clarity with a slightly more premium executive tone so overviews feel decisive instead of purely utilitarian.',
    },
    moodBoard: {
      keywords: ['elevated metrics', 'balanced navy and gold', 'summary cards', 'leadership reporting'],
      imageHints: ['executive analytics workspace navy gold', 'boardroom dashboard metrics screen', 'management reporting interface premium', 'modern corporate overview cards'],
    },
    tokens: {
      colors: {
        primary: '#1E3A8A',
        background: '#F6F5F2',
        surface: '#FFFCF6',
        text: '#1D2433',
        muted: '#6B7280',
        success: '#2C8C62',
        warning: '#B98118',
        danger: '#B94A48',
      },
      radius: 20,
      spacing: 8,
      density: 'medium',
    },
  },
  {
    id: 'dark-control-room',
    name: 'Dark Control Room',
    category: 'internal-ops',
    description: 'Long-session dark interface with contained accents for advanced internal operators and focused workflows.',
    extractionNotes: {
      basedOnConcepts: ['context-fit UX', 'contrast discipline', 'semantic state prioritization', 'anti-pattern checks'],
      adaptedFor: 'Supports long, high-focus sessions with restrained accent colors and surfaces that still feel enterprise-ready rather than flashy.',
    },
    moodBoard: {
      keywords: ['dark operations center', 'cool slate glass', 'precise signals', 'monitoring calm'],
      imageHints: ['dark operations dashboard screens', 'night office control room', 'slate industrial workspace interface', 'monitoring station blue charcoal'],
    },
    tokens: {
      colors: {
        primary: '#7AA2FF',
        background: '#0E1726',
        surface: '#142033',
        text: '#E6EDF7',
        muted: '#90A0B8',
        success: '#2EB67D',
        warning: '#F59E0B',
        danger: '#F87171',
      },
      radius: 18,
      spacing: 8,
      density: 'medium',
    },
  },
  {
    id: 'minimal-mono',
    name: 'Minimal Mono',
    category: 'internal-ops',
    description: 'Typography-led, restrained, and sharp for highly professional internal tools with minimal visual noise.',
    extractionNotes: {
      basedOnConcepts: ['typography pairing logic', 'style taxonomy', 'clarity over decoration', 'component consistency'],
      adaptedFor: 'Creates a disciplined internal tool language where type hierarchy and spacing do most of the work, ideal for task-heavy professional workflows.',
    },
    moodBoard: {
      keywords: ['editorial monochrome', 'paper-white surfaces', 'quiet hierarchy', 'architectural precision'],
      imageHints: ['minimal monochrome workspace desk', 'editorial black white interface', 'architect studio clean neutral', 'professional document layout minimal'],
    },
    tokens: {
      colors: {
        primary: '#0F172A',
        background: '#FAFAF8',
        surface: '#FFFFFF',
        text: '#10131A',
        muted: '#646A73',
        success: '#2E7D5A',
        warning: '#A96B18',
        danger: '#AF3C3C',
      },
      radius: 10,
      spacing: 8,
      density: 'low',
    },
  },
  {
    id: 'field-clear',
    name: 'Field Clear',
    category: 'field-operations',
    description: 'Mobile-first, direct, high-clarity styling for check-in, check-out, and daily field usage.',
    extractionNotes: {
      basedOnConcepts: ['mobile context-fit', 'semantic action hierarchy', 'accessibility-conscious contrast', 'workflow-driven style selection'],
      adaptedFor: 'Designed for fast thumb interactions in bright, busy environments where the next action must always be obvious.',
    },
    moodBoard: {
      keywords: ['clear action blocks', 'bright daylight readability', 'mobile utility', 'clean status cues'],
      imageHints: ['construction mobile app daylight', 'field operations smartphone checklist', 'site worker phone interface clear', 'outdoor utility app readable'],
    },
    tokens: {
      colors: {
        primary: '#0F62FE',
        background: '#F3F8FF',
        surface: '#FFFFFF',
        text: '#0F172A',
        muted: '#64748B',
        success: '#138A4B',
        warning: '#D97706',
        danger: '#D92D20',
      },
      radius: 20,
      spacing: 8,
      density: 'medium',
    },
  },
  {
    id: 'construction-rugged',
    name: 'Construction Rugged',
    category: 'field-operations',
    description: 'Slightly industrial and block-forward for site operations with stronger visual containment and rugged confidence.',
    extractionNotes: {
      basedOnConcepts: ['industry-specific style matching', 'utility palette logic', 'durable component patterns', 'task-oriented UX guidance'],
      adaptedFor: 'Fits site context by using stronger surfaces and a workwear palette while staying clean enough for repeated operational use.',
    },
    moodBoard: {
      keywords: ['industrial utility', 'charcoal and safety orange', 'heavy blocks', 'site signage clarity'],
      imageHints: ['construction site signage orange charcoal', 'industrial mobile interface rugged', 'site office tablet operations', 'workwear color palette utility app'],
    },
    tokens: {
      colors: {
        primary: '#B45309',
        background: '#F4F1EB',
        surface: '#FFF9F2',
        text: '#1F2937',
        muted: '#6B7280',
        success: '#1C7C54',
        warning: '#D97706',
        danger: '#C2410C',
      },
      radius: 14,
      spacing: 8,
      density: 'high',
    },
  },
  {
    id: 'friendly-flow',
    name: 'Friendly Flow',
    category: 'field-operations',
    description: 'Softer, more approachable direction for workers and public-facing operational flows that still need clarity.',
    extractionNotes: {
      basedOnConcepts: ['tone selection by audience', 'semantic color grouping', 'supportive UX guidance', 'clarity-first component reuse'],
      adaptedFor: 'Introduces warmth and approachability for broader user groups without sacrificing operational confidence or task visibility.',
    },
    moodBoard: {
      keywords: ['approachable mobile cards', 'teal and coral warmth', 'gentle rounded forms', 'friendly task flow'],
      imageHints: ['friendly service app mobile', 'community check in interface warm', 'soft operational ui teal coral', 'approachable kiosk flow people'],
    },
    tokens: {
      colors: {
        primary: '#147D72',
        background: '#F5FBFA',
        surface: '#FFFFFF',
        text: '#153047',
        muted: '#6A7A89',
        success: '#1C8C5E',
        warning: '#D4881A',
        danger: '#D95D5D',
      },
      radius: 24,
      spacing: 8,
      density: 'low',
    },
  },
  {
    id: 'kiosk-mode',
    name: 'Kiosk Mode',
    category: 'field-operations',
    description: 'Tap-first, oversized, and low-cognitive-load for shared devices and fast repeat actions.',
    extractionNotes: {
      basedOnConcepts: ['workflow-specific patterning', 'interaction sizing rules', 'reduced cognitive load', 'accessibility and anti-pattern checks'],
      adaptedFor: 'Optimized for public/shared-device use where large targets, minimal copy, and unmistakable status feedback matter most.',
    },
    moodBoard: {
      keywords: ['oversized controls', 'clean kiosks', 'high visibility', 'single-purpose interaction'],
      imageHints: ['self service kiosk interface clean', 'tablet check in station large buttons', 'touchscreen operations simple', 'public kiosk bright clarity'],
    },
    tokens: {
      colors: {
        primary: '#00594C',
        background: '#F3F7F5',
        surface: '#FFFFFF',
        text: '#0F172A',
        muted: '#61717F',
        success: '#18834E',
        warning: '#B7791F',
        danger: '#C53030',
      },
      radius: 28,
      spacing: 8,
      density: 'low',
    },
  },
  {
    id: 'emergency-priority',
    name: 'Emergency Priority',
    category: 'field-operations',
    description: 'Alert-oriented interface with strong status hierarchy for incidents, exceptions, and urgent action flows.',
    extractionNotes: {
      basedOnConcepts: ['status-first semantics', 'risk-driven hierarchy', 'decision-speed UX guidance', 'anti-pattern prevention'],
      adaptedFor: 'Uses contrast and signal colors to surface urgency immediately while preserving clear recovery and confirmation states.',
    },
    moodBoard: {
      keywords: ['urgent action surfaces', 'red amber signal hierarchy', 'incident response mobile', 'high contrast utility'],
      imageHints: ['emergency response dashboard mobile', 'incident alert interface red amber', 'urgent operations tablet screen', 'safety control mobile high contrast'],
    },
    tokens: {
      colors: {
        primary: '#C62828',
        background: '#FFF6F4',
        surface: '#FFFFFF',
        text: '#22181A',
        muted: '#756A6C',
        success: '#2E7D32',
        warning: '#E67E22',
        danger: '#B71C1C',
      },
      radius: 16,
      spacing: 8,
      density: 'high',
    },
  },
];

export const styleDefinitionMap = Object.fromEntries(
  styleDefinitions.map((style) => [style.id, style]),
) as Record<string, StyleDefinition>;

export function getStyleById(id: string): StyleDefinition | undefined {
  return styleDefinitionMap[id];
}

export function getStylesByCategory(category: StyleCategory): StyleDefinition[] {
  return styleDefinitions.filter((style) => style.category === category);
}

export function getProductStyles(product: ProductKey): StyleDefinition[] {
  return getStylesByCategory(PRODUCT_CATEGORY_MAP[product]);
}
